import React, { useState } from 'react'
import { X, Mail, Lock, User, MapPin, Award, Stethoscope, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { FormFieldStatus } from './FeedbackIndicators'
import { LoadingButton } from './LoadingStates'
import { ValidatedInput, ValidatedCheckbox } from './FormValidation'
import { showToast } from '../utils/toast';

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'signin' | 'signup'
  onModeChange: (mode: 'signin' | 'signup') => void
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, mode, onModeChange }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [location, setLocation] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isExpertSignup, setIsExpertSignup] = useState(false)
  const [profession, setProfession] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({})
  
  const { signIn, signUp } = useAuth()

  const professions = [
    'Medical Doctor (MD)',
    'Pharmacist',
    'Registered Nurse',
    'Clinical Psychologist',
    'Nutritionist/Dietitian',
    'Physical Therapist',
    'Dentist',
    'Optometrist',
    'Public Health Specialist',
    'Mental Health Counselor'
  ]

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email) ? null : 'Please enter a valid email address'
  }

  const validatePassword = (password: string) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters'
    }
    return null
  }

  const validateForm = () => {
    const errors: {[key: string]: string} = {}
    
    if (!email) errors.email = 'Email is required'
    else {
      const emailError = validateEmail(email)
      if (emailError) errors.email = emailError
    }
    
    if (!password) errors.password = 'Password is required'
    else {
      const passwordError = validatePassword(password)
      if (passwordError) errors.password = passwordError
    }
    
    if (mode === 'signup') {
      if (!fullName) errors.fullName = 'Full name is required'
      
      if (isExpertSignup) {
        if (!profession) errors.profession = 'Profession is required'
        if (!licenseNumber) errors.licenseNumber = 'License number is required'
        if (!termsAccepted) errors.terms = 'You must accept the terms'
      }
    }
    
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormSubmitted(true)
    
    if (!validateForm()) {
      return
    }
    
    setLoading(true)
    setError('')

    try {
      if (mode === 'signup') {
        const userData = {
          full_name: fullName,
          location,
          ...(isExpertSignup && {
            is_expert: true,
            profession,
            license_number: licenseNumber,
            verification_status: 'partial_pending'
          })
        }

        const { error } = await signUp(email, password, userData)
        if (error) throw error
        
        // 2. Replaced alerts with toasts
        showToast.auth.signUpSuccess();
        if (isExpertSignup) {
          showToast.expert.verificationSubmitted();
        }
      } else {
        const { error } = await signIn(email, password)
        if (error) throw error
        
        // 4. Added success feedback on sign-in
        showToast.auth.signInSuccess();
        onClose()
      }
    } catch (error: any) {
      // 3. Replaced error state with authError toast
      showToast.auth.authError(error.message)
    } finally {
      setLoading(false)
    }
  }
 
  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-900 dark:shadow-xl dark:border dark:border-gray-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-custom animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {mode === 'signin' ? 'Welcome Back' : 
             isExpertSignup ? 'Join as Health Expert' : 'Join PharmPlus'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
            disabled={loading}
          >
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-950 dark:border-red-800 border border-red-200 rounded-lg p-3 animate-fade-in">
              <div className="flex items-center">
                <AlertTriangle className="text-red-600 dark:text-red-400 mr-2" size={18} />
                <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Expert Signup Toggle - Only show during signup */}
          {mode === 'signup' && !isExpertSignup && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 dark:border-green-800 border border-green-200 rounded-xl p-4 animate-fade-in">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <Stethoscope className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 dark:text-green-100">Healthcare Professional?</h4>
                  <p className="text-green-700 dark:text-green-300 text-sm">Join as a verified expert and share your knowledge</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsExpertSignup(true)}
                className="w-full btn-primary"
                disabled={loading}
              >
                I am a healthcare professional seeking expert verification
              </button>
            </div>
          )}

          {/* Expert Signup Header */}
          {isExpertSignup && (
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-900 dark:to-emerald-900 rounded-xl p-4 text-white mb-4 animate-fade-in">
              <div className="flex items-center space-x-3 mb-2">
                <Award size={24} />
                <h4 className="font-bold">Expert Account Setup</h4>
              </div>
              <p className="text-green-100 dark:text-green-200 text-sm">
                Create your expert account and complete verification in your profile after signup.
              </p>
              <button
                type="button"
                onClick={() => setIsExpertSignup(false)}
                className="text-green-200 hover:text-white dark:text-green-200 dark:hover:text-white text-sm mt-2 underline"
                disabled={loading}
              >
                ← Back to regular signup
              </button>
            </div>
          )}
          
          {mode === 'signup' && (
            <>
              <ValidatedInput
                id="fullName"
                label="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required={true}
                placeholder={isExpertSignup ? "Dr. Ama Osei" : "Enter your full name"}
                validate={(value) => value.length < 2 ? "Name must be at least 2 characters" : null}
                showValidationIcon={formSubmitted}
                autoComplete="name"
                disabled={loading}
              />
              
              <ValidatedInput
                id="location"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Accra, Ghana"
                showValidationIcon={formSubmitted}
                autoComplete="address-level1"
                disabled={loading}
              />

              {/* Expert-specific fields */}
              {isExpertSignup && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profession *</label>
                    <select
                      required
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      className={`select-base ${fieldErrors.profession && formSubmitted ? 'input-error' : ''}`}
                      disabled={loading}
                    >
                      <option value="">Select your profession</option>
                      {professions.map(prof => (
                        <option key={prof} value={prof}>{prof}</option>
                      ))}
                    </select>
                    {fieldErrors.profession && formSubmitted && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.profession}</p>
                    )}
                  </div>

                  <ValidatedInput
                    id="licenseNumber"
                    label="License Number"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    required={true}
                    placeholder="e.g., MDC/12345"
                    showValidationIcon={formSubmitted}
                    disabled={loading}
                  />

                  <div className="bg-blue-50 dark:bg-blue-950 dark:border-blue-800 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <Info className="text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" size={16} />
                      <p className="text-blue-800 dark:text-blue-300 text-sm">
                        <strong>Next Steps:</strong> After creating your account, you'll be able to complete your full expert verification in your profile, including uploading credentials and setting your specializations.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          
          <ValidatedInput
            id="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required={true}
            placeholder="Enter your email"
            validate={validateEmail}
            showValidationIcon={formSubmitted}
            autoComplete="email"
            disabled={loading}
          />
          
          <ValidatedInput
            id="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required={true}
            placeholder="Enter your password"
            minLength={6}
            validate={validatePassword}
            showValidationIcon={formSubmitted}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            disabled={loading}
          />
          
          {isExpertSignup && (
            <ValidatedCheckbox
              id="terms"
              label={
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  I agree to the <a href="#" className="text-green-600 hover:underline dark:text-green-400 dark:hover:text-green-300">Terms of Service</a> and <a href="#" className="text-green-600 hover:underline dark:text-green-400 dark:hover:text-green-300">Expert Code of Conduct</a>
                </span>
              }
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required={true}
              disabled={loading}
            />
          )}
          
          <LoadingButton
            type="submit"
            isLoading={loading}
            loadingText={
              mode === 'signin' ? 'Signing In...' : 
              isExpertSignup ? 'Creating Expert Account...' : 'Creating Account...'
            }
            disabled={loading}
            className="w-full py-3 rounded-xl font-medium"
            variant="primary"
          >
            {mode === 'signin' ? 'Sign In' : 
             isExpertSignup ? 'Create Expert Account' : 'Create Account'}
          </LoadingButton>
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => onModeChange(mode === 'signin' ? 'signup' : 'signin')}
              className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium"
              disabled={loading}
            >
              {mode === 'signin' 
                ? "Don't have an account? Sign up" 
                : "Already have an account? Sign in"
              }
            </button>
          </div>
          
          {mode === 'signin' && (
            <div className="text-center mt-2">
              <button
                type="button"
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => {
                  showToast.system.info('Password reset functionality coming soon!');
                }}
                disabled={loading}
              >
                Forgot your password?
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default AuthModal